import { Cat, KINDS } from '../../../gameplay/registry';
import { Res } from '../../../gameplay/types';
import { FX_ONE, isqrt } from '../fixed';
import { Order, UState, type World } from '../world';
import { approach, setDestination, setDirect, stopMoving } from './movement';

/** Gatherers work from up to one tile away so many can share a tree, mine or bush. */
const REACH = FX_ONE;
/** Gatherers walk right up to a tree, mine, bush or animal before working it. */
const CLOSE = Math.trunc(FX_ONE * 0.3);
const SEEK_RADIUS = 10 * FX_ONE;
/** Fishing boats range further: fish shoals are spread across open water. */
const FISH_SEEK_RADIUS = 18 * FX_ONE;

/** Resource type yielded by a gatherable entity (resource node or farm), or -1. */
export function resourceOf(w: World, t: number): number {
  const k = KINDS[w.kind[t]];
  if (k.resource) return k.resource.res;
  if (k.building?.foodAmount !== undefined && w.built[t]) return Res.Food;
  return -1;
}

/** Fish can only be harvested by ships; everything else only by land gatherers. */
export function gatherableBy(w: World, s: number, t: number): boolean {
  const fish = KINDS[w.kind[t]].resource?.model === 'fish';
  return fish === (w.naval[s] === 1);
}

export function isFarm(w: World, t: number): boolean {
  return KINDS[w.kind[t]].building?.foodAmount !== undefined;
}

/** Find nearest completed drop-off building for resource r owned by player. */
export function findDropoff(w: World, s: number, r: number): number {
  let best = -1;
  let bestD = Infinity;
  const naval = w.naval[s] === 1;
  for (const i of w.dropoffsOf(w.owner[s])) {
    const d = KINDS[w.kind[i]].building!.dropoff!;
    if (!d.includes(r)) continue;
    if (naval && KINDS[w.kind[i]].key !== 'dock') continue;
    const dist = w.edgeDist(i, w.x[s], w.y[s]);
    if (dist < bestD) {
      bestD = dist;
      best = i;
    }
  }
  return best;
}

/** Find nearest resource of the same kind family (same resource type) near a point. */
export function findNearbyResource(w: World, s: number, r: number, x: number, y: number, preferKind: number): number {
  let best = -1;
  let bestD = Infinity;
  const owner = w.owner[s];
  w.forStaticInRadius(x, y, w.naval[s] ? FISH_SEEK_RADIUS : SEEK_RADIUS, (o, d2) => {
    if (resourceOf(w, o) !== r) return;
    if (!gatherableBy(w, s, o)) return;
    if (w.isUnreachable(owner, w.idOf(o))) return;
    if (w.cat[o] === Cat.Building && w.owner[o] !== owner) return;
    // Explored-only.
    const vis = w.visibility[owner];
    if (vis && vis[(w.y[o] >> 10) * w.size + (w.x[o] >> 10)] === 0) return;
    // Prefer the same kind of resource, and farms nobody is working yet (sharing is allowed).
    const shared = w.cat[o] === Cat.Building && w.resolve(w.worker[o]) >= 0 ? 9 * FX_ONE * FX_ONE : 0;
    const pen = (w.kind[o] === preferKind ? 0 : 4 * FX_ONE * FX_ONE) + shared;
    const score = d2 + pen;
    if (score < bestD || (score === bestD && o < best)) {
      bestD = score;
      best = o;
    }
  });
  return best;
}

export function orderGather(w: World, s: number, t: number): void {
  w.order[s] = Order.Gather;
  w.target[s] = w.idOf(t);
  w.lastResKind[s] = w.kind[t];
  const r = resourceOf(w, t);
  if (w.carryAmt[s] > 0 && w.carryRes[s] !== r) {
    // Drop carried resource of another type (as in classic RTS).
    w.carryAmt[s] = 0;
  }
  if (isFarm(w, t) && w.resolve(w.worker[t]) < 0) w.worker[t] = w.idOf(s);
  w.state[s] = UState.Walking;
  if (isFarm(w, t)) setDestination(w, s, w.x[t], w.y[t]);
  else approach(w, s, t);
}

function returnResources(w: World, s: number): void {
  const d = findDropoff(w, s, w.carryRes[s]);
  if (d < 0) {
    w.state[s] = UState.Idle;
    stopMoving(w, s);
    return;
  }
  w.state[s] = UState.Returning;
  approach(w, s, d);
}

/** Idle fishing boats are fishermen: every couple of seconds they look for fish near where they are. */
function autoFish(w: World): void {
  for (let s = 0; s < w.highSlot; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit || !w.naval[s] || w.order[s] !== Order.None || w.moving[s]) continue;
    if ((w.tick + s) % 40 !== 0 || w.owner[s] < 0) continue;
    if (!KINDS[w.kind[s]].unit!.gatherRates) continue;
    const t = findNearbyResource(w, s, Res.Food, w.x[s], w.y[s], -1);
    if (t >= 0) orderGather(w, s, t);
  }
}

export function updateEconomy(w: World): void {
  autoFish(w);
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit || w.order[s] !== Order.Gather) continue;
    const owner = w.owner[s];
    const p = w.players[owner];
    const st = p.stats[w.kind[s]];
    const st8 = w.state[s];
    let t = w.resolve(w.target[s]);

    if (st8 === UState.Returning) {
      const d = findDropoff(w, s, w.carryRes[s]);
      if (d < 0 || w.isUnreachable(owner, w.idOf(d))) {
        w.state[s] = UState.Idle;
        w.order[s] = Order.None;
        continue;
      }
      if (w.edgeDist(d, w.x[s], w.y[s]) <= REACH) {
        const r = w.carryRes[s];
        p.res[r] += w.carryAmt[s];
        p.gathered[r] += w.carryAmt[s];
        w.carryAmt[s] = 0;
        stopMoving(w, s);
        if (t < 0 || resourceOf(w, t) < 0) {
          t = findNearbyResource(w, s, r, w.x[s], w.y[s], w.lastResKind[s]);
          if (t < 0) {
            w.order[s] = Order.None;
            w.state[s] = UState.Idle;
            continue;
          }
          w.target[s] = w.idOf(t);
          if (isFarm(w, t) && w.resolve(w.worker[t]) < 0) w.worker[t] = w.idOf(s);
        }
        w.state[s] = UState.Walking;
        if (isFarm(w, t)) setDestination(w, s, w.x[t], w.y[t]);
        else approach(w, s, t);
      } else if (!w.moving[s]) {
        approach(w, s, d);
      }
      continue;
    }

    if (t >= 0 && w.isUnreachable(owner, w.target[s])) t = -1;
    if (t < 0 || resourceOf(w, t) < 0) {
      // Target gone or unreachable: seek a replacement of the same resource.
      const r = w.lastResKind[s] >= 0 ? (KINDS[w.lastResKind[s]].resource?.res ?? Res.Food) : -1;
      const nt = r >= 0 ? findNearbyResource(w, s, r, w.x[s], w.y[s], w.lastResKind[s]) : -1;
      if (nt < 0) {
        if (w.carryAmt[s] > 0) returnResources(w, s);
        else {
          w.order[s] = Order.None;
          w.state[s] = UState.Idle;
          stopMoving(w, s);
        }
        continue;
      }
      w.target[s] = w.idOf(nt);
      if (isFarm(w, nt) && w.resolve(w.worker[nt]) < 0) w.worker[nt] = w.idOf(s);
      w.state[s] = UState.Walking;
      if (isFarm(w, nt)) setDestination(w, s, w.x[nt], w.y[nt]);
      else approach(w, s, nt);
      continue;
    }

    const farm = isFarm(w, t);
    // Close contact; the looser one-tile reach only applies once the gatherer has stopped (other
    // workers are crowding the spot), so a busy mine still takes many villagers.
    const ed = w.edgeDist(t, w.x[s], w.y[s]);
    const inReach = farm ? ed <= FX_ONE / 4 : ed <= CLOSE || (ed <= REACH && !w.moving[s]);
    let reachedNear = false;
    if (!inReach) {
      if (!w.moving[s] && !w.paths[s]) {
        if (farm) setDestination(w, s, w.x[t], w.y[t]);
        else if (ed <= REACH + (FX_ONE >> 1)) {
          // Arrived next to it (often on a diagonal tile): step straight in rather than searching
          // for a new route, which would lead back to the same spot.
          const dx = w.x[t] - w.x[s];
          const dy = w.y[t] - w.y[s];
          const d = Math.max(1, isqrt(dx * dx + dy * dy));
          const go = Math.max(0, ed - (CLOSE >> 1));
          // Nothing to close (already against its tile): work from here.
          if (!setDirect(w, s, w.x[s] + Math.trunc((dx * go) / d), w.y[s] + Math.trunc((dy * go) / d)) || go < (FX_ONE >> 3)) reachedNear = true;
        } else approach(w, s, t);
      }
      if (!reachedNear) {
        w.state[s] = UState.Walking;
        continue;
      }
      stopMoving(w, s);
    }
    if (w.moving[s] && !farm) stopMoving(w, s);
    if (farm && w.moving[s]) {
      // Walk to centre of farm and work there.
      continue;
    }
    w.timer[s] = 0;
    w.state[s] = UState.Gathering;
    const r = resourceOf(w, t);
    if (w.carryRes[s] !== r) {
      w.carryRes[s] = r;
      w.carryAmt[s] = 0;
    }
    const dx = w.x[t] - w.x[s];
    const dy = w.y[t] - w.y[s];
    if (dx !== 0 || dy !== 0) {
      const dd = Math.max(1, Math.floor(Math.sqrt(dx * dx + dy * dy)));
      w.fdx[s] = Math.trunc((dx * 1000) / dd);
      w.fdy[s] = Math.trunc((dy * 1000) / dd);
    }
    const kres = KINDS[w.kind[t]].resource;
    const rate = kres?.model === 'deer' ? st.huntGather : kres?.model === 'sheep' ? Math.trunc((st.gather[r] * 110) / 100) : st.gather[r];
    const take = Math.min(rate, w.resAmt[t], st.carry * FX_ONE - w.carryAmt[s]);
    w.carryAmt[s] += take;
    // Farms never wear out; every other resource is used up.
    if (!farm) w.resAmt[t] -= take;
    if ((w.tick + s) % 20 === 0) w.events.push({ e: 'gatherTick', owner, res: r, x: w.x[s], y: w.y[s] });
    if (!farm && w.resAmt[t] <= 0) depleteResource(w, t, owner);
    if (w.carryAmt[s] >= st.carry * FX_ONE) returnResources(w, s);
  }
}

function depleteResource(w: World, t: number, owner: number): void {
  if (isFarm(w, t)) {
    const p = w.players[w.owner[t]];
    const cost = p.stats[w.kind[t]].cost;
    if (p.autoReseed && p.res[1] >= cost[1] * FX_ONE) {
      p.res[1] -= cost[1] * FX_ONE;
      w.resAmt[t] = (KINDS[w.kind[t]].building!.foodAmount! + p.global.farmFood) * FX_ONE;
      return;
    }
    w.events.push({ e: 'depleted', owner: w.owner[t], x: w.x[t], y: w.y[t], res: Res.Food });
    w.kill(t);
    return;
  }
  const r = KINDS[w.kind[t]].resource!.res;
  if (r === Res.Gold || r === Res.Stone) w.events.push({ e: 'depleted', owner, x: w.x[t], y: w.y[t], res: r });
  w.kill(t);
}
